import { Box, Button, Typography } from "@mui/material";
import { motion, AnimatePresence } from "motion/react";
import { Trophy, X } from "lucide-react";
import { useState, ChangeEvent, FormEvent } from "react";
import { palette } from "../theme";
import { ContestManagement, ContestManagementDisc } from "../generated/core-types";

interface ContestBannerProps {
  contest: ContestManagement;
  onStart: (keyword: string) => void;
  onStop: () => void;
  matchCount: number;
}

export function ContestBanner({
  contest,
  onStart,
  onStop,
  matchCount,
}: ContestBannerProps): JSX.Element {
  const [keyword, setKeyword] = useState<string>("");
  const ongoing = contest.type === ContestManagementDisc.Contest;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (keyword.trim().length > 0) onStart(keyword.trim());
  };
  const onKeyword = (e: ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value);

  return (
    <AnimatePresence mode="wait" initial={false}>
      {ongoing ? (
        <motion.div
          key="ongoing"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.24, ease: [0.2, 0, 0.2, 1] }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: 3,
              py: 1.5,
              backgroundColor: palette.plumBg,
              border: `1px solid ${palette.plum}`,
              borderRadius: 10,
              color: palette.plum,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Trophy size={18} strokeWidth={1.6} />
              <Typography variant="overline" sx={{ color: palette.plum }}>
                Cuộc thi đang diễn ra
              </Typography>
              <Box
                component="span"
                sx={{
                  fontFamily: '"Fraunces Variable", Georgia, serif',
                  fontStyle: "italic",
                  fontSize: "1.05rem",
                  color: palette.ink,
                }}
              >
                « {contest.keyword} »
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: "0.8rem",
                  color: palette.inkSoft,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                · {matchCount} lượt tham gia
              </Box>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={onStop}
              startIcon={<X size={14} strokeWidth={1.8} />}
              sx={{
                borderColor: palette.plum,
                color: palette.plum,
                "&:hover": {
                  borderColor: palette.plum,
                  backgroundColor: "rgba(122,78,111,0.08)",
                },
              }}
            >
              Dừng
            </Button>
          </Box>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              px: 3,
              py: 1.5,
              backgroundColor: palette.surface,
              border: `1px dashed ${palette.border}`,
              borderRadius: 10,
            }}
          >
            <Trophy size={16} strokeWidth={1.6} color={palette.inkSoft} />
            <Typography variant="overline" sx={{ color: palette.inkSoft }}>
              Cuộc thi
            </Typography>
            <Box
              component="input"
              value={keyword}
              onChange={onKeyword}
              placeholder="từ khóa cuộc thi…"
              sx={{
                flex: 1,
                minWidth: 200,
                border: "none",
                outline: "none",
                background: "transparent",
                fontFamily: '"Fraunces Variable", Georgia, serif',
                fontStyle: "italic",
                fontSize: "1rem",
                color: palette.ink,
                "::placeholder": { color: palette.inkSoft, opacity: 0.7 },
              }}
            />
            <Button
              type="submit"
              size="small"
              variant="contained"
              disabled={keyword.trim().length === 0}
              sx={{
                backgroundColor: palette.plum,
                color: palette.surface,
                "&:hover": { backgroundColor: "#5e3a55" },
                "&.Mui-disabled": { backgroundColor: palette.border, color: palette.inkSoft },
              }}
            >
              Bắt đầu
            </Button>
          </Box>
        </motion.form>
      )}
    </AnimatePresence>
  );
}
